import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API_URL from "../../apiConfig";
import AdminHeader from "../../components/AdminHeader";

const STATUS_LABELS = {
  interested: "Interesado",
  contacted: "Contactado",
  converted: "Convertido",
  withdrawn: "Retirado",
};

export default function AdminFounderEditionInterests() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("loading");

  async function load() {
    setStatus("loading");
    try {
      const response = await fetch(`${API_URL}/api/founder-edition-interests/admin?productSlug=mente-en-direccion`, {
        credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok !== true) throw new Error(data?.error || "load_failed");
      setItems(Array.isArray(data.interests) ? data.interests : []);
      setStats(data.stats || null);
      setStatus("ready");
    } catch (error) {
      console.error("[ADMIN_FOUNDER_EDITION_INTERESTS_LOAD]", error);
      setStatus("error");
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function changeStatus(id, nextStatus) {
    try {
      const response = await fetch(`${API_URL}/api/founder-edition-interests/admin/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok !== true) throw new Error(data?.error || "update_failed");
      setItems((current) => current.map((item) => (item.id === id ? data.interest : item)));
      await load();
    } catch (error) {
      console.error("[ADMIN_FOUNDER_EDITION_INTERESTS_UPDATE]", error);
      window.alert("No fue posible actualizar el estado.");
    }
  }

  const summary = useMemo(() => [
    ["Total", stats?.total ?? 0],
    ["Interesados", stats?.interested ?? 0],
    ["Contactados", stats?.contacted ?? 0],
    ["Convertidos", stats?.converted ?? 0],
    ["Confirmación enviada", stats?.confirmed ?? 0],
  ], [stats]);

  return (
    <>
      <AdminHeader />
      <main style={styles.page}>
        <div style={styles.topRow}>
          <Link to="/admin" style={styles.back}>← Volver a Oficina Central</Link>
        </div>

        <div style={styles.eyebrow}>Crecimiento · Edición Fundadora</div>
        <h1 style={styles.title}>Mente en Dirección</h1>
        <p style={styles.lead}>Personas que registraron explícitamente su interés en recibir información sobre la Edición Fundadora.</p>

        <section style={styles.statsGrid}>
          {summary.map(([label, value]) => (
            <div key={label} style={styles.statCard}>
              <div style={styles.statValue}>{value}</div>
              <div style={styles.statLabel}>{label}</div>
            </div>
          ))}
        </section>

        {status === "loading" ? <div style={styles.notice}>Cargando...</div> : null}
        {status === "error" ? <div style={styles.error}>No fue posible cargar el listado.</div> : null}

        {status === "ready" ? (
          <section style={styles.tableCard}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Fecha</th>
                    <th style={styles.th}>Nombre</th>
                    <th style={styles.th}>Correo</th>
                    <th style={styles.th}>Idioma</th>
                    <th style={styles.th}>Origen</th>
                    <th style={styles.th}>Confirmación</th>
                    <th style={styles.th}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td style={styles.td}>{item.created_at ? new Date(item.created_at).toLocaleString("es-CA") : "—"}</td>
                      <td style={styles.td}>{item.name || "—"}</td>
                      <td style={styles.td}>{item.email}</td>
                      <td style={styles.td}>{String(item.language || "es").toUpperCase()}</td>
                      <td style={styles.td}>{item.source || "—"}</td>
                      <td style={styles.td}>{item.confirmation_sent_at ? "Sí" : "Pendiente"}</td>
                      <td style={styles.td}>
                        <select value={item.status} onChange={(event) => changeStatus(item.id, event.target.value)} style={styles.select}>
                          {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {!items.length ? (
                    <tr><td colSpan="7" style={styles.empty}>Todavía no hay registros.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </main>
    </>
  );
}

const styles = {
  page: { maxWidth: 1180, margin: "2rem auto 4rem", padding: "0 16px", fontFamily: "Questrial, sans-serif" },
  topRow: { display: "flex", alignItems: "center", marginBottom: 18 },
  back: { color: "#264763", textDecoration: "none", fontWeight: 700, fontSize: 14 },
  eyebrow: { fontSize: 12, textTransform: "uppercase", letterSpacing: ".13em", color: "#CCA352", fontWeight: 800 },
  title: { margin: "6px 0 8px", color: "#264763", fontFamily: "Bitter, serif", fontSize: 34 },
  lead: { margin: 0, color: "#607486", lineHeight: 1.6 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, margin: "22px 0" },
  statCard: { background: "#fff", border: "1px solid #e3e9ee", borderRadius: 14, padding: 16 },
  statValue: { fontSize: 28, fontWeight: 900, color: "#264763" },
  statLabel: { marginTop: 5, color: "#6b7a88", fontSize: 13 },
  tableCard: { background: "#fff", border: "1px solid #e3e9ee", borderRadius: 16, overflow: "hidden" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 920 },
  th: { textAlign: "left", background: "#f7f9fb", color: "#526b7f", padding: "12px 14px", fontSize: 12, borderBottom: "1px solid #e3e9ee" },
  td: { padding: "13px 14px", color: "#364f63", fontSize: 14, borderBottom: "1px solid #edf1f4", verticalAlign: "top" },
  select: { border: "1px solid #cfd9e1", borderRadius: 8, padding: "7px 9px", background: "#fff" },
  empty: { padding: 28, textAlign: "center", color: "#7b8b98" },
  notice: { padding: 18, background: "#f7f9fb", borderRadius: 12 },
  error: { padding: 18, background: "#fff5f5", color: "#9b2c2c", borderRadius: 12 },
};
